/**
 * Canais de origem das vendas (Trilha F, F4): lista editável de onde os contatos chegam
 * (Instagram, Meta Ads, indicação...). Vive em Configurações — não é tela nova.
 *
 * Regras (migration 0062, `sales_origin_options`):
 *  - desativar tira o canal do formulário de venda, mas pedidos antigos continuam com o slug
 *    e seguem legíveis nos relatórios; por isso NÃO existe botão de apagar;
 *  - origens de sistema (Anúncio Meta, site) são preenchidas pela captura automática: podem
 *    ser desativadas, mas o painel avisa que a captura deixa de marcar a conversa;
 *  - o slug é derivado do nome no servidor; nome repetido volta 409 e a tela mostra o motivo.
 *
 * View pura (props) + `SalesOriginsPanel` que liga aos hooks, no padrão das demais telas.
 */
import React, { useState, type ReactElement } from "react";
import { Button, controlClass } from "../ui/index.js";
import { StatusPanel, errorDetail } from "./StatusPanel.js";
import { useCreateSalesOrigin, useSalesOrigins, useUpdateSalesOrigin } from "../queries.js";
import type { SalesOriginOption } from "../orders/acquisition.js";

export interface SalesOriginsViewProps {
  origins: readonly SalesOriginOption[];
  onCreate: (label: string) => Promise<unknown>;
  onToggle: (slug: string, active: boolean) => Promise<unknown>;
  busy?: boolean;
}

/** Mensagem legível para o erro cru do servidor (`origin_already_exists:/api/...:409`). */
function originErrorMessage(error: unknown): string {
  const detail = errorDetail(error);
  if (detail.includes("origin_already_exists")) return "Já existe um canal com esse nome.";
  if (detail.includes("invalid_label")) return "Informe um nome com pelo menos 2 letras.";
  return detail;
}

export function SalesOriginsView({ origins, onCreate, onToggle, busy = false }: SalesOriginsViewProps): ReactElement {
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function add(): Promise<void> {
    setError(null);
    try {
      await onCreate(label.trim());
      setLabel("");
    } catch (failure) {
      setError(originErrorMessage(failure));
    }
  }

  async function toggle(slug: string, active: boolean): Promise<void> {
    setError(null);
    try {
      await onToggle(slug, active);
    } catch (failure) {
      setError(originErrorMessage(failure));
    }
  }

  return (
    <section className="flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1" data-section="canais-origem">
      <div>
        <h3 className="text-lg font-extrabold text-ink">Canais de origem das vendas</h3>
        <p className="m-0 text-sm text-muted">
          Canais desativados deixam de aparecer no formulário de venda, mas os registros antigos continuam preservados no histórico.
        </p>
      </div>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (label.trim().length >= 2) void add();
        }}
      >
        <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-sm font-semibold text-ink">
          Nome do novo canal
          <input
            className={controlClass}
            value={label}
            maxLength={120}
            placeholder="Ex.: Contato pelo Kwai"
            data-origin-field="label"
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <Button variant="primary" type="submit" disabled={busy || label.trim().length < 2} data-origin-action="adicionar">
          Adicionar
        </Button>
      </form>
      {error ? (
        <p role="alert" className="m-0 text-sm font-semibold text-danger" data-origin="erro">
          {error}
        </p>
      ) : null}
      <ul className="m-0 flex list-none flex-col divide-y divide-border p-0" aria-label="Canais de origem">
        {origins.map((origin) => (
          <li key={origin.slug} className="flex flex-wrap items-center justify-between gap-3 py-2" data-origin-slug={origin.slug}>
            <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
              {origin.label}
              {origin.system ? <span className="ml-2 text-xs font-normal text-muted">preenchido pela captura automática</span> : null}
            </span>
            <label className="flex items-center gap-2 text-sm text-body">
              <input
                type="checkbox"
                role="switch"
                checked={origin.active}
                disabled={busy}
                aria-label={`${origin.label}: ${origin.active ? "ativo" : "inativo"}`}
                data-origin-toggle={origin.slug}
                onChange={(event) => void toggle(origin.slug, event.target.checked)}
              />
              {origin.active ? "Ativo" : "Inativo"}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function SalesOriginsPanel(): ReactElement | null {
  const query = useSalesOrigins();
  const create = useCreateSalesOrigin();
  const update = useUpdateSalesOrigin();
  if (query.isPending) return <StatusPanel kind="loading" title="Carregando canais de origem…" />;
  if (query.error || !query.data) {
    // Rota ainda não publicada (ou erro): declara a ausência em vez de mostrar uma lista vazia.
    return (
      <StatusPanel
        kind="awaiting_source"
        title="Canais de origem não disponíveis neste ambiente"
        detail="A rota /api/platform/acquisition/origins não respondeu. Ela depende da migration 0062 e da função platform-orders-runtime publicadas."
      />
    );
  }
  return (
    <SalesOriginsView
      origins={query.data}
      busy={create.isPending || update.isPending}
      onCreate={(label) => create.mutateAsync(label)}
      onToggle={(slug, active) => update.mutateAsync({ slug, active })}
    />
  );
}
